import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import {
  Activity, Clock, Zap, CheckCircle, RefreshCw, AlertTriangle,
  TrendingUp, ArrowUpRight, Server, Shield, Wifi, ChevronDown, ChevronUp,
  Mail, Bell, Settings, Send, CheckCircle2, History
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const HEALTH_COLORS = {
  excellent: { ring: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', glow: 'shadow-emerald-500/20' },
  good: { ring: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30', glow: 'shadow-blue-500/20' },
  warning: { ring: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', glow: 'shadow-amber-500/20' },
  critical: { ring: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', glow: 'shadow-red-500/20' },
};

const STATUS_COLORS = { "2xx": "#22c55e", "3xx": "#3b82f6", "4xx": "#f59e0b", "5xx": "#ef4444" };

function HealthRing({ score, status }) {
  const c = HEALTH_COLORS[status] || HEALTH_COLORS.good;
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className={`relative w-36 h-36 ${c.glow} shadow-lg rounded-full`} data-testid="health-ring">
      <svg className="w-36 h-36 -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(100,116,139,0.15)" strokeWidth="8" />
        <circle cx="60" cy="60" r="54" fill="none" className={c.ring}
          stroke="currentColor" strokeWidth="8" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1s ease-in-out' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-white">{score}</span>
        <span className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">
          {status === 'excellent' ? 'ممتاز' : status === 'good' ? 'جيد' : status === 'warning' ? 'تحذير' : 'حرج'}
        </span>
      </div>
    </div>
  );
}

function LivePulse({ rps, avgMs, errors, requests }) {
  return (
    <div className="flex items-center gap-4 flex-wrap" data-testid="live-stats">
      <div className="flex items-center gap-2">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
        </span>
        <span className="text-xs text-slate-400">مباشر</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Wifi className="w-3.5 h-3.5 text-blue-400" />
        <span className="text-sm font-semibold text-white">{rps}</span>
        <span className="text-[10px] text-slate-500">طلب/ث</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5 text-amber-400" />
        <span className="text-sm font-semibold text-white">{avgMs}</span>
        <span className="text-[10px] text-slate-500">ms</span>
      </div>
      <div className="flex items-center gap-1.5">
        <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
        <span className="text-sm font-semibold text-white">{errors}</span>
        <span className="text-[10px] text-slate-500">خطأ / 5 دقائق</span>
      </div>
    </div>
  );
}

function EndpointRow({ ep, isExpanded, onToggle }) {
  const statusColor = ep.status === 'healthy' ? 'text-emerald-400 bg-emerald-500/10' :
    ep.status === 'degraded' ? 'text-amber-400 bg-amber-500/10' : 'text-red-400 bg-red-500/10';
  const methodColor = ep.method === 'POST' ? 'text-blue-400' : ep.method === 'PUT' ? 'text-amber-400' :
    ep.method === 'DELETE' ? 'text-red-400' : 'text-emerald-400';

  return (
    <div className="border-b border-slate-800/40 last:border-b-0">
      <div className="flex items-center gap-3 py-2.5 px-3 cursor-pointer hover:bg-slate-800/20 transition-colors" onClick={onToggle}>
        <span className={`text-[10px] font-mono font-bold ${methodColor} w-10`}>{ep.method}</span>
        <code className="text-[11px] text-slate-300 font-mono flex-1 truncate">{ep.endpoint}</code>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColor}`}>
          {ep.status === 'healthy' ? 'سليم' : ep.status === 'degraded' ? 'متراجع' : 'حرج'}
        </span>
        <span className="text-xs text-slate-400 w-16 text-left">{ep.avg_ms}ms</span>
        <span className="text-[10px] text-slate-500 w-12 text-left">{ep.total_calls}x</span>
        {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
      </div>
      {isExpanded && (
        <div className="px-3 pb-3 grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-900/30">
          <div><p className="text-[10px] text-slate-500">P95</p><p className="text-sm font-semibold text-white">{ep.p95_ms}ms</p></div>
          <div><p className="text-[10px] text-slate-500">أقصى</p><p className="text-sm font-semibold text-white">{ep.max_ms}ms</p></div>
          <div><p className="text-[10px] text-slate-500">أدنى</p><p className="text-sm font-semibold text-white">{ep.min_ms}ms</p></div>
          <div><p className="text-[10px] text-slate-500">نسبة الخطأ</p>
            <p className={`text-sm font-semibold ${ep.error_rate > 5 ? 'text-red-400' : 'text-emerald-400'}`}>{ep.error_rate}%</p>
          </div>
        </div>
      )}
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#151b2e] border border-slate-700/60 rounded-lg px-3 py-2 shadow-xl text-xs" dir="rtl">
      <p className="text-slate-400 mb-1 font-mono">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-white">
          <span className="inline-block w-2 h-2 rounded-full me-1.5" style={{ backgroundColor: p.color }} />
          {p.name === 'avg_ms' ? 'متوسط: ' : p.name === 'requests' ? 'طلبات: ' : 'أخطاء: '}
          <span className="font-semibold">{p.value}{p.name === 'avg_ms' ? 'ms' : ''}</span>
        </p>
      ))}
    </div>
  );
}

function AlertSettingsPanel({ API, authHeaders }) {
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/alert-settings`, authHeaders()).then(r => r.data).catch(() => null),
      axios.get(`${API}/alert-history`, authHeaders()).then(r => r.data).catch(() => ({ history: [] })),
    ]).then(([s, h]) => {
      if (s) setSettings(s);
      if (h) setHistory(h.history || []);
    }).finally(() => setLoading(false));
  }, [API, authHeaders]);

  const save = async (updates) => {
    const newSettings = { ...settings, ...updates };
    setSettings(newSettings);
    setSaving(true);
    try {
      await axios.put(`${API}/alert-settings`, updates, authHeaders());
    } catch {}
    setSaving(false);
  };

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/alert-settings/test`, {}, authHeaders());
      setTestResult({ ok: res.data.success, email: res.data.sent_to });
    } catch {
      setTestResult({ ok: false });
    }
    setTesting(false);
  };

  if (loading || !settings) return null;

  return (
    <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl overflow-hidden" data-testid="alert-settings-panel">
      <div className="p-4 border-b border-slate-800/40 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-white flex items-center gap-2">
          <Mail className="w-4 h-4 text-amber-400" />
          إعدادات تنبيهات البريد الإلكتروني
        </h2>
        <div className="flex items-center gap-2">
          {saving && <span className="text-[10px] text-amber-400 animate-pulse">جاري الحفظ...</span>}
          <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)}
            className="text-xs text-slate-400 hover:text-white" data-testid="toggle-alert-history">
            <History className="w-3.5 h-3.5 me-1" /> السجل
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Enable toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-300">تفعيل التنبيهات بالبريد</span>
          </div>
          <button data-testid="toggle-email-alerts"
            onClick={() => save({ enabled: !settings.enabled })}
            className={`w-10 h-5 rounded-full transition-colors relative ${settings.enabled ? 'bg-emerald-500' : 'bg-slate-700'}`}>
            <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-all ${settings.enabled ? 'right-0.5' : 'right-[22px]'}`} />
          </button>
        </div>

        {settings.enabled && (
          <>
            {/* Recipient email */}
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">البريد الإلكتروني للمستلم</label>
              <input type="email" value={settings.recipient_email || ''} data-testid="alert-email-input"
                onChange={(e) => setSettings(s => ({ ...s, recipient_email: e.target.value }))}
                onBlur={(e) => save({ recipient_email: e.target.value })}
                className="w-full bg-slate-900/60 border border-slate-700/60 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
                placeholder="admin@example.com" dir="ltr" />
            </div>

            {/* Health threshold */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs text-slate-400">حد صحة النظام للتنبيه</label>
                <span className="text-sm font-semibold text-amber-400" data-testid="threshold-value">{settings.health_threshold}</span>
              </div>
              <input type="range" min="20" max="95" step="5" value={settings.health_threshold}
                data-testid="health-threshold-slider"
                onChange={(e) => setSettings(s => ({ ...s, health_threshold: parseInt(e.target.value) }))}
                onMouseUp={(e) => save({ health_threshold: parseInt(e.target.value) })}
                onTouchEnd={(e) => save({ health_threshold: parseInt(e.target.value) })}
                className="w-full h-1.5 bg-slate-700 rounded-full appearance-none cursor-pointer accent-amber-500" />
              <div className="flex justify-between text-[9px] text-slate-600 mt-1">
                <span>20 (حساس جداً)</span>
                <span>95 (حرج فقط)</span>
              </div>
            </div>

            {/* Cooldown */}
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">فترة التبريد بين التنبيهات</label>
              <div className="flex gap-2">
                {[15, 30, 60, 120].map(min => (
                  <button key={min} data-testid={`cooldown-${min}`}
                    onClick={() => save({ cooldown_minutes: min })}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${settings.cooldown_minutes === min ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-slate-800/40 text-slate-400 border border-slate-700/40 hover:border-slate-600'}`}>
                    {min} د
                  </button>
                ))}
              </div>
            </div>

            {/* Notification types */}
            <div className="space-y-2">
              <label className="text-xs text-slate-400 block">أنواع الإشعارات</label>
              {[
                { key: 'notify_on_critical', label: 'عند حالة حرجة', icon: '🔴' },
                { key: 'notify_on_warning', label: 'عند حالة تحذير', icon: '🟡' },
                { key: 'notify_on_recovery', label: 'عند التعافي', icon: '🟢' },
              ].map(item => (
                <label key={item.key} className="flex items-center gap-2.5 cursor-pointer group">
                  <input type="checkbox" checked={settings[item.key] ?? true}
                    data-testid={`toggle-${item.key}`}
                    onChange={(e) => save({ [item.key]: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/30" />
                  <span className="text-xs text-slate-300 group-hover:text-white">{item.icon} {item.label}</span>
                </label>
              ))}
            </div>

            {/* Test button + Last sent info */}
            <div className="flex items-center gap-3 pt-2 border-t border-slate-800/40">
              <Button variant="ghost" size="sm" onClick={sendTest} disabled={testing} data-testid="send-test-alert"
                className="text-xs text-amber-400 hover:text-amber-300 border border-amber-500/20 hover:border-amber-500/40">
                {testing ? <RefreshCw className="w-3.5 h-3.5 me-1 animate-spin" /> : <Send className="w-3.5 h-3.5 me-1" />}
                إرسال تنبيه تجريبي
              </Button>
              {testResult && (
                <span className={`text-[10px] ${testResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>
                  {testResult.ok ? `تم الإرسال إلى ${testResult.email}` : 'فشل الإرسال'}
                </span>
              )}
              {settings.last_alert_sent && (
                <span className="text-[10px] text-slate-500 ms-auto">
                  آخر تنبيه: {new Date(settings.last_alert_sent).toLocaleString('ar-SA')}
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Alert History */}
      {showHistory && history.length > 0 && (
        <div className="border-t border-slate-800/40 p-4" data-testid="alert-history-list">
          <h3 className="text-xs font-semibold text-slate-400 mb-2">سجل التنبيهات المُرسلة</h3>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {history.map((h, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] py-1.5 border-b border-slate-800/30 last:border-b-0">
                {h.success ? <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" /> : <AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0" />}
                <span className="text-slate-300">درجة {h.health_score}</span>
                <span className={`px-1.5 py-0.5 rounded text-[9px] ${h.health_status === 'critical' ? 'bg-red-500/10 text-red-400' : h.health_status === 'warning' ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                  {h.reason === 'recovery' ? 'تعافي' : h.reason === 'critical' ? 'حرج' : 'تجاوز الحد'}
                </span>
                <span className="text-slate-600 ms-auto font-mono">{h.sent_at?.slice(0, 16)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPerformancePage() {
  const { authHeaders, API } = useAdminAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [perfData, setPerfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEp, setExpandedEp] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/monitoring`, authHeaders()).then(r => r.data).catch(() => null),
      axios.get(`${API}/performance`, authHeaders()).then(r => r.data).catch(() => null),
    ]).then(([mon, perf]) => {
      if (mon) setData(mon);
      if (perf) setPerfData(perf);
    }).finally(() => setLoading(false));
  }, [API, authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  if (loading && !data) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const d = data || {};
  const health = d.health || { score: 100, status: 'excellent' };
  const live = d.live || {};
  const today = d.today || {};
  const statusBreakdown = d.status_breakdown || {};
  const endpoints = d.endpoint_details || [];
  const minuteTrend = d.minute_trend || [];
  const alerts = d.active_alerts || [];
  const hourly = perfData?.hourly_performance || [];

  const pieData = Object.entries(statusBreakdown).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k, value: v }));

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="admin-performance-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-red-400" />
              مراقبة أداء النظام
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">مراقبة مباشرة لأداء جميع APIs والتنبيهات</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" data-testid="toggle-auto-refresh"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`text-xs ${autoRefresh ? 'text-emerald-400' : 'text-slate-500'}`}>
              {autoRefresh ? 'تحديث تلقائي' : 'إيقاف التحديث'}
            </Button>
            <Button variant="ghost" size="sm" onClick={fetchData} data-testid="refresh-perf-btn"
              className="text-slate-400 hover:text-white">
              <RefreshCw className="w-4 h-4 me-1" /> تحديث
            </Button>
          </div>
        </div>

        {/* Health Score + Live Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-5">
          <div className={`bg-[#0d1224] border ${HEALTH_COLORS[health.status]?.border || 'border-slate-800/60'} rounded-xl p-5 flex flex-col items-center gap-3`}>
            <HealthRing score={health.score} status={health.status} />
            <p className="text-xs text-slate-400">صحة النظام</p>
          </div>

          <div className="space-y-4">
            {/* Live pulse */}
            <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl p-4">
              <LivePulse rps={live.rps} avgMs={live.avg_ms} errors={live.errors_5min} requests={live.requests_5min} />
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl p-3.5" data-testid="perf-total-requests">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Zap className="w-3.5 h-3.5 text-blue-400" />
                  </div>
                </div>
                <p className="text-xl font-bold text-white">{today.total_requests || 0}</p>
                <p className="text-[10px] text-slate-400">إجمالي الطلبات</p>
              </div>
              <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl p-3.5" data-testid="perf-avg-response">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Clock className="w-3.5 h-3.5 text-amber-400" />
                  </div>
                </div>
                <p className="text-xl font-bold text-white">{today.avg_response_ms || 0}<span className="text-xs text-slate-500 ms-1">ms</span></p>
                <p className="text-[10px] text-slate-400">متوسط الاستجابة</p>
              </div>
              <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl p-3.5" data-testid="perf-success-rate">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                  </div>
                </div>
                <p className="text-xl font-bold text-white">{today.success_rate || 100}<span className="text-xs text-slate-500 ms-1">%</span></p>
                <p className="text-[10px] text-slate-400">معدل النجاح</p>
              </div>
              <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl p-3.5" data-testid="perf-error-count">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                  </div>
                </div>
                <p className="text-xl font-bold text-white">{today.error_count || 0}</p>
                <p className="text-[10px] text-slate-400">أخطاء اليوم</p>
              </div>
            </div>
          </div>
        </div>

        {/* Minute Trend Chart */}
        <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl p-5" data-testid="minute-trend-chart">
          <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-blue-400" />
            حركة الطلبات (آخر ساعة)
          </h2>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={minuteTrend}>
              <defs>
                <linearGradient id="reqGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="msGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="minute" tick={{ fill: '#475569', fontSize: 9 }} interval={9} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#475569', fontSize: 9 }} axisLine={false} tickLine={false} width={35} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="requests" stroke="#3b82f6" fill="url(#reqGrad)" strokeWidth={1.5} name="requests" />
              <Area type="monotone" dataKey="avg_ms" stroke="#f59e0b" fill="url(#msGrad)" strokeWidth={1.5} name="avg_ms" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Status Breakdown + Hourly Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          {/* Status Pie */}
          <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl p-4" data-testid="status-breakdown">
            <h2 className="text-sm font-semibold text-white mb-3">توزيع حالات الاستجابة</h2>
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={3} dataKey="value">
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.name] || '#64748b'} />
                  ))}
                </Pie>
                <Tooltip content={({ active, payload }) => active && payload?.length ? (
                  <div className="bg-[#151b2e] border border-slate-700/60 rounded-lg px-3 py-1.5 text-xs">
                    <span className="text-white font-semibold">{payload[0].name}: {payload[0].value}</span>
                  </div>
                ) : null} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-3 justify-center">
              {Object.entries(STATUS_COLORS).map(([k, color]) => (
                <span key={k} className="flex items-center gap-1 text-[10px] text-slate-400">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                  {k} ({statusBreakdown[k] || 0})
                </span>
              ))}
            </div>
          </div>

          {/* Hourly bar chart */}
          <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl p-4" data-testid="hourly-chart">
            <h2 className="text-sm font-semibold text-white mb-3">متوسط الاستجابة بالساعة (24 ساعة)</h2>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={hourly}>
                <XAxis dataKey="hour" tick={{ fill: '#475569', fontSize: 9 }} interval={3} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#475569', fontSize: 9 }} axisLine={false} tickLine={false} width={35} />
                <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
                  <div className="bg-[#151b2e] border border-slate-700/60 rounded-lg px-3 py-1.5 text-xs">
                    <p className="text-slate-400 font-mono">{label}</p>
                    <p className="text-white">متوسط: <span className="font-semibold">{payload[0]?.value}ms</span></p>
                    <p className="text-slate-400">طلبات: {payload[1]?.value || 0}</p>
                  </div>
                ) : null} />
                <Bar dataKey="avg_ms" radius={[3, 3, 0, 0]} maxBarSize={20}>
                  {hourly.map((entry, i) => (
                    <Cell key={i} fill={entry.avg_ms > 1000 ? 'rgba(239,68,68,0.5)' : entry.avg_ms > 500 ? 'rgba(245,158,11,0.5)' : 'rgba(34,197,94,0.4)'} />
                  ))}
                </Bar>
                <Bar dataKey="count" radius={[3, 3, 0, 0]} maxBarSize={20} fill="rgba(59,130,246,0.2)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Endpoint Details Table */}
        <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl overflow-hidden" data-testid="endpoint-details">
          <div className="p-4 border-b border-slate-800/40 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <Server className="w-4 h-4 text-slate-400" />
              تفاصيل الـ Endpoints ({endpoints.length})
            </h2>
            <div className="flex gap-2 text-[10px]">
              <span className="flex items-center gap-1 text-emerald-400">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> سليم
              </span>
              <span className="flex items-center gap-1 text-amber-400">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400" /> متراجع
              </span>
              <span className="flex items-center gap-1 text-red-400">
                <div className="w-1.5 h-1.5 rounded-full bg-red-400" /> حرج
              </span>
            </div>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {endpoints.length === 0 ? (
              <p className="text-center text-xs text-slate-600 py-8">لا توجد بيانات بعد - استخدم التطبيق لتوليد المقاييس</p>
            ) : endpoints.map((ep, i) => (
              <EndpointRow key={i} ep={ep} isExpanded={expandedEp === i} onToggle={() => setExpandedEp(expandedEp === i ? null : i)} />
            ))}
          </div>
        </div>

        {/* Active Alerts */}
        {alerts.length > 0 && (
          <div className="bg-[#0d1224] border border-red-500/20 rounded-xl p-4" data-testid="active-alerts">
            <h2 className="text-sm font-semibold text-red-400 mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              تنبيهات نشطة ({alerts.length})
            </h2>
            <div className="space-y-2">
              {alerts.map((a, i) => (
                <div key={i} className={`flex items-start gap-3 p-2.5 rounded-lg ${a.severity === 'critical' ? 'bg-red-500/5' : 'bg-amber-500/5'}`}>
                  <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${a.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white font-medium">{a.title_ar || a.title_en}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{a.message_ar || a.message_en}</p>
                    <p className="text-[9px] text-slate-600 mt-1 font-mono">{a.timestamp?.slice(0, 19)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Email Alert Settings */}
        <AlertSettingsPanel API={API} authHeaders={authHeaders} />
      </div>
    </AdminLayout>
  );
}
